import { toast } from "sonner";

interface VersionInfo {
  version: string;
  timestamp: string;
  buildNumber: string;
}

const VERSION_STORAGE_KEY = 'app_version';
const LAST_CHECK_KEY = 'version_last_check';
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

/**
 * Fetch the current deployed version from version.json
 */
export async function fetchDeployedVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    
    if (!response.ok) {
      // File doesn't exist (expected in dev/before first deployment)
      return null;
    }
    
    // Check Content-Type to ensure we got JSON
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // Got HTML or other non-JSON response (e.g., index.html fallback)
      return null;
    }
    
    // Get response text to verify it's valid JSON before parsing
    const text = await response.text();
    if (!text.trim().startsWith('{')) {
      // Not valid JSON (probably HTML)
      return null;
    }
    
    const versionInfo: VersionInfo = JSON.parse(text);
    console.log('📦 Deployed version:', versionInfo);
    return versionInfo;
  } catch (error) {
    // Only log if it's an actual network error, not a parsing error
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.warn('Network error fetching version:', error);
    }
    return null;
  }
}

/**
 * Get the stored local version from localStorage
 */
function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (error) {
    console.warn('Error reading stored version:', error);
    return null;
  }
}

/**
 * Store the current version in localStorage
 */
function storeVersion(version: string): void {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Error storing version:', error);
  }
}

/**
 * Check if enough time has passed since last check
 */
function shouldCheckVersion(): boolean {
  try {
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    if (!lastCheck) return true;
    
    const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
    return timeSinceLastCheck > CHECK_INTERVAL;
  } catch (error) {
    return true;
  }
}

/**
 * Reload the page with cache busting
 */
export function reloadPage(): void {
  // Clear service worker caches if available
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }
  
  // Hard reload with cache bypass
  window.location.reload();
}

/**
 * Show update notification to user
 */
function showUpdateNotification(): void {
  toast("🎉 Nieuwe versie beschikbaar", {
    description: "Er is een nieuwe versie van de applicatie beschikbaar.",
    action: {
      label: "Vernieuwen",
      onClick: reloadPage,
    },
    duration: Infinity, // Don't auto-dismiss
  });
}

/**
 * Check for version updates and notify if new version is available
 */
export async function checkForUpdates(silent: boolean = false): Promise<boolean> {
  // Skip if checked recently (unless forced)
  if (!silent && !shouldCheckVersion()) {
    return false;
  }
  
  const deployedVersion = await fetchDeployedVersion();
  if (!deployedVersion) {
    return false;
  }
  
  const storedVersion = getStoredVersion();
  
  // First time visiting or no stored version
  if (!storedVersion) {
    console.log('📦 First visit, storing version:', deployedVersion.version);
    storeVersion(deployedVersion.version);
    return false;
  }
  
  // Check if version has changed
  if (storedVersion !== deployedVersion.version) {
    console.log('🔄 Version mismatch detected:', {
      stored: storedVersion,
      deployed: deployedVersion.version,
    });
    
    if (!silent) {
      showUpdateNotification();
    }
    
    // Update stored version
    storeVersion(deployedVersion.version);
    return true;
  }
  
  // Update last check time
  if (!silent) {
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
  }
  
  return false;
}

/**
 * Initialize version checking with visibility change listener
 */
export function initializeVersionCheck(): void {
  // Skip version checking in development mode
  if (import.meta.env.DEV) {
    console.log('⚠️ Version checking disabled in development mode');
    return;
  }
  
  // Check on initial load
  checkForUpdates(true).then(hasUpdate => {
    if (!hasUpdate) {
      console.log('✅ App version is up to date');
    }
  });
  
  // Check when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      console.log('👁️ Tab visible, checking for updates...');
      checkForUpdates();
    }
  });
  
  // Periodic check while tab is active
  setInterval(() => {
    if (document.visibilityState === 'visible') {
      checkForUpdates();
    }
  }, CHECK_INTERVAL);
  
  console.log('🔍 Version checking initialized');
}
